/**
 * Чистые операции над матрицей прав роли: `{раздел: [действия]}`.
 *
 * Правило ERPNext: любое действие подразумевает просмотр. Включили «Изменение» — включается
 * и «Просмотр»; сняли «Просмотр» — строка очищается целиком. То же правило навязывает бэкенд
 * (`access/services/roles.py::normalize_matrix`), здесь оно нужно, чтобы UI не показывал
 * состояние, которое сервер всё равно исправит.
 */

export const READ = "read";
export const ACTION_ORDER = ["read", "create", "write", "delete", "submit", "cancel", "export"];

function ordered(set) {
  return ACTION_ORDER.filter((a) => set.has(a));
}

function withRow(matrix, resourceKey, set) {
  const next = { ...matrix };
  if (set.size) next[resourceKey] = ordered(set);
  else delete next[resourceKey];
  return next;
}

export function hasAction(matrix, resourceKey, action) {
  return Boolean(matrix?.[resourceKey]?.includes(action));
}

/**
 * Переключает одну ячейку. `resource` — элемент реестра (`{key, used_actions}`).
 * Включаемо любое из `ACTION_ORDER`, даже если раздел пока его не проверяет (`used_actions`).
 */
export function setCell(matrix, resource, action, on) {
  if (!ACTION_ORDER.includes(action)) return matrix;
  const set = new Set(matrix[resource.key] || []);
  if (on) {
    set.add(action);
    set.add(READ);
  } else if (action === READ) {
    set.clear();
  } else {
    set.delete(action);
  }
  return withRow(matrix, resource.key, set);
}

/** Все действия раздела — вкл/выкл (клик по названию строки). */
export function setRow(matrix, resource, on) {
  return withRow(matrix, resource.key, new Set(on ? ACTION_ORDER : []));
}

/** Одно действие у набора разделов (колонка или колонка внутри секции). */
export function setColumn(matrix, resources, action, on) {
  return resources.reduce((acc, r) => setCell(acc, r, action, on), matrix);
}

/** Состояние группы ячеек для чекбокса «выбрать всё»: all / some / none. */
export function columnState(matrix, resources, action) {
  if (!resources.length) return "none";
  const on = resources.filter((r) => hasAction(matrix, r.key, action)).length;
  if (on === 0) return "none";
  return on === resources.length ? "all" : "some";
}

export function rowState(matrix, resource) {
  const on = (matrix[resource.key] || []).length;
  if (on === 0) return "none";
  return on === ACTION_ORDER.length ? "all" : "some";
}

/** Отличие клетки от базы: `added` (есть, в базе нет), `removed` (в базе есть, снято) или `null`. */
export function cellDiff(matrix, baseline, resourceKey, action) {
  if (!baseline) return null;
  const now = hasAction(matrix, resourceKey, action);
  const before = hasAction(baseline, resourceKey, action);
  if (now === before) return null;
  return now ? "added" : "removed";
}

export function matrixEquals(a, b) {
  const keys = new Set([...Object.keys(a || {}), ...Object.keys(b || {})]);
  for (const k of keys) {
    const x = ordered(new Set(a?.[k] || []));
    const y = ordered(new Set(b?.[k] || []));
    if (x.join() !== y.join()) return false;
  }
  return true;
}

/** Сколько разделов доступно на просмотр — для сводки «доступно N из M разделов». */
export function countReadable(matrix) {
  return Object.values(matrix || {}).filter((actions) => actions.includes(READ)).length;
}
